import { useServiceGovernance } from '@/hooks/useServiceGovernance';
import { useToast } from '@/hooks/use-toast';
import { useCallback } from 'react';

/**
 * Returns a wrapper that checks governance before executing an action.
 * If the service is blocked, shows a toast with the governance message
 * and prevents the action from executing.
 */
export function useGovernedAction(serviceKey: string) {
  const governance = useServiceGovernance(serviceKey);
  const { toast } = useToast();

  const guardedAction = useCallback(
    <Args extends unknown[], Result>(action: (...args: Args) => Result) => {
      return (...args: Args): Result | undefined => {
        if (!governance.allowed) {
          toast({
            title: 'Action Blocked',
            description: governance.message || 'This service is currently unavailable.',
            variant: 'destructive',
          });
          return undefined;
        }
        return action(...args);
      };
    },
    [governance.allowed, governance.message, toast]
  );

  return {
    ...governance,
    guardedAction,
    /** Whether this service is in read-only mode (historical data visible, no mutations) */
    isReadOnly: governance.isReadOnly,
    /** Whether the action should be completely hidden vs shown-but-disabled */
    shouldHide: governance.isGlobalOff,
    /** Whether the action should be shown but disabled with a message */
    shouldDisable: !governance.allowed,
  };
}
